import { supabase } from '../lib/supabase';
import { activityService } from './activityService';
import { notificationService } from './notificationService';
import { DEMO_ERROR_MESSAGE, isDemoUser } from '../utils/demoMode';

export const incidentService = {
  async createIncident(incidentData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const { data, error } = await supabase?.from('incidents')?.insert({
          site_id: incidentData?.siteId,
          company_id: incidentData?.companyId,
          incident_type: incidentData?.incidentType,
          incident_date: incidentData?.incidentDate,
          location_description: incidentData?.locationDescription,
          description: incidentData?.description,
          severity_level: incidentData?.severityLevel,
          injured_party: incidentData?.injuredParty || null,
          witness_name: incidentData?.witnessName || null,
          witness_contact: incidentData?.witnessContact || null,
          immediate_action_taken: incidentData?.immediateActionTaken || null,
          corrective_measures: incidentData?.correctiveMeasures || null,
          file_paths: incidentData?.filePaths || [],
          reported_by: user?.id,
          is_read_only: false
        })?.select()?.single();

      if (error) throw error;

      // Log activity
      if (data?.site_id) {
        await activityService?.createActivity({
          companyId: data?.company_id,
          siteId: data?.site_id,
          activityType: 'incident_created',
          title: `Incident reported: ${incidentData?.incidentType?.replace(/_/g, ' ')}`,
          description: `Severity: ${incidentData?.severityLevel || 'unknown'} | Location: ${incidentData?.locationDescription || 'N/A'}`,
          severityLevel: incidentData?.severityLevel || null,
          relatedEntityId: data?.id,
          metadata: { incidentId: data?.id, severity: incidentData?.severityLevel, type: incidentData?.incidentType }
        });
      }

      // Notify admins/site managers of incident report
      notificationService?.notifyIncidentReported(data?.id, data?.site_id, data?.company_id);

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // NSherjFh PR 5: default-filters soft-deleted incidents (deleted_at IS
  // NULL). Pass `{ includeDeleted: true }` from the incident-reporting
  // 'View deleted' tab. Mirrors PR 2-4 opt-in shape exactly.
  // NB: severity_level / status are SEPARATE axes from deleted_at —
  // a closed/resolved incident is still active; a soft-deleted incident
  // is hidden regardless of severity or status.
  async getIncidentsBySite(siteId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('incidents')
        ?.select('*, user_profiles!incidents_reported_by_fkey(full_name)')
        ?.eq('site_id', siteId)
        ?.order('incident_date', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getAllIncidents(options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('incidents')
        ?.select('*, sites(site_name), user_profiles!incidents_reported_by_fkey(full_name)')
        ?.order('incident_date', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async uploadIncidentFile(file, companyId, siteId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `incidents/${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${siteId}/${fileName}`;

      const { error: uploadError } = await supabase?.storage?.from('construction-documents')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;
      return { filePath, error: null };
    } catch (error) {
      return { filePath: null, error: { message: error?.message } };
    }
  },

  async updateIncidentStatus(id, status) {
    try {
      const { data, error } = await supabase?.from('incidents')?.update({ status })?.eq('id', id)?.select()?.single();
      if (error) throw error;
      // Notify admins/site managers of status change
      if (data?.site_id && data?.company_id) {
        notificationService?.notifyIncidentStatusChanged(data?.id, data?.site_id, data?.company_id, status);
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // NSherjFh PR 5 — recoverable soft-delete on the incident.
  //
  // Built from scratch (no existing delete method or UI). Stamps
  // deleted_at + deleted_by; idempotent via .is('deleted_at', null);
  // returns .maybeSingle() so a second click resolves to null data
  // without error.
  //
  // is_read_only state is NOT consulted — the flag is a UI-only
  // informational badge (no DB constraint enforces it) and admin
  // cleanup is a separate gesture from editing content.
  //
  // file_paths array on the row stays intact; attached files in the
  // construction-documents bucket are preserved. Storage hits in
  // sitePhotoGalleryService now filter parent deleted_at so the
  // photos disappear from the gallery while the parent is deleted.
  async deleteIncident(incidentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }
      const { data, error } = await supabase
        ?.from('incidents')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', incidentId)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Inverse of deleteIncident. Clears deleted_at/_by so the incident
  // reappears in every active surface (incident-reporting list, site-
  // detail safety tab, compliance dashboard counts, client portal).
  // file_paths attachments resurface automatically via the parent
  // filter unblocking.
  async restoreIncident(incidentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }
      const { data, error } = await supabase
        ?.from('incidents')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', incidentId)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  }
};